import { useCallback, useContext, useEffect, useMemo, useState } from "react";
// components
import { TrashIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import { Link, useNavigate } from "react-router-dom";

import {
  CONSULT_REVIEW_FREEBIE,
  FORM_FACTOR_TO_IMAGE_V2,
  TRANSPARENT_BUNDLE_FREEBIES,
} from "@/components/care/constants";
import { ReactComponent as SyncIcon } from "@/components/care/icons/sync.svg";
// images
import CarePackagingImg from "@/components/care/images/care-packaging-no-bg.png";
import RainingPrescriptionsImg from "@/components/care/images/raining-prescriptions.png";
import { setBottomCtaOccupied } from "@/components/care/transparentCare/bottomCtaOccupancy";
// utils
import {
  formatNumberToPriceString,
  formatResolvedPriceString,
  getCareIntakeUrl,
  getCarePageVisitedLocalStorageKey,
} from "@/components/care/utils";
import BlueRectangularButton from "@/components/common/blueRectangularButton";
import ErrorBanner from "@/components/common/errorBanner";
import { EvvyMarkdown } from "@/components/common/evvyMarkdown";
import { ReactComponent as CheckMarkIcon } from "@/components/common/icons/checkmark.svg";
import TestKitImg from "@/components/common/images/horizontalImages/test-kit.png";
import LoadingSpinner from "@/components/common/loadingSpinner";
import Panel from "@/components/common/panel";
import RectangularButton from "@/components/common/rectangularButton";
import { registerCartPanel } from "@/components/ecomm/cartPanelPresence";
import {
  EXPANDED_TEST_SKU,
  UTI_TEST_SKU,
  VHT_TEST_SKU,
} from "@/components/ecomm/constants";
import { CartContext } from "@/contexts/cartContext";
// hooks
import { useTransparentCareTreatmentsContext } from "@/contexts/transparentCareTreatmentsContext";
import { usePanelCart } from "@/hooks/cart/usePanelCart";
import { SUPPORT_EMAIL } from "@/pages/constants";
import { MALE_PARTNER_PRODUCT_COPY } from "@/pages/malePartner/constants";
import UtiTestImg from "@/pages/shopAll/assets/utiTest.png";
import VhtUtiBundleImg from "@/pages/shopAll/assets/vhtUtiBundleTest.png";
// services
import { careService } from "@/services/care";
import { cartService } from "@/services/cart";
import { subscriptionService } from "@/services/subscription";
import {
  EcommProduct,
  PharmacyType,
  Prescription,
  TransparentCareRecommendations,
} from "@/types/care";
// types
import {
  AddCartItemPayload,
  Cart,
  CART_ITEM_TYPE,
  CartFreebieItem,
  CartItem,
  SubscriptionInfo,
  TreatmentBundle,
  UtiBundle,
} from "@/types/cart";
import { Test } from "@/types/test";
import {
  eventNames,
  sendCareProgramCheckoutStarted,
  sendClickedTestUpsellCTA,
  sendTreatmentCartClickedCheckout,
  sendTreatmentCartClickedViewMoreProducts,
  sendTreatmentCartViewedCart,
  sendTreatmentClickedEmptyStateCTA,
} from "@/utils/analytics/customEventTracking";
import { trackCustomEvent } from "@/utils/analytics/tracking";
import { redirectToCheckout } from "@/utils/checkout";
import { cn } from "@/utils/cn";
import { formatDate, strDateToTimestamp } from "@/utils/datetime";
import { orderMetrics } from "@/utils/newrelicHelpers";

// Helper function to identify male partner treatment voucher items
const isMalePartnerTreatmentVoucher = (item: CartItem): boolean => {
  const product = item.ecomm_product;
  if (!product) return false;

  // Check by SKU (most reliable)
  if (product.sku === MALE_PARTNER_PRODUCT_COPY.DEFAULT_PRODUCT.SKU) {
    return true;
  }

  // Check by name patterns as fallback
  const productName = product.name?.toLowerCase() || "";
  return (
    productName.includes("male partner") &&
    (productName.includes("treatment") || productName.includes("voucher"))
  );
};

type BundleCartItemProps = {
  items: CartItem[];
  productItems: CartItem[];
  addToCart: (items: AddCartItemPayload[]) => void;
  isLoading: boolean;
  removeFromCart: (items: CartItem[]) => void;
  refetchCart: () => void;
  disabled?: boolean;
  isRemoving?: boolean;
  initializeExpanded: boolean;
  subscriptionInfo: SubscriptionInfo;
  baseCartAnalyticsArgs: any;
  bundleContainsEligibleSubscriptionItems?: boolean;
  bundleSubscriptionState?: {
    state: "none" | "some" | "all";
    subscribedCount: number;
    totalSubscribableCount: number;
  };
  handleBundleSubscriptionChange?: (checked: boolean) => void;
  onItemSubscriptionChange?: (
    item: CartItem,
    subscribe: boolean
  ) => Promise<boolean>;
  treatmentBundle: TreatmentBundle | null;
};

const UserCart = ({
  cart,
  vaginalTest,
  closeCart,
  addToCart,
  removeFromCart,
  baseCartAnalyticsArgs,
  isLoading,
}: {
  cart?: Cart;
  vaginalTest?: Test;
  closeCart: () => void;
  addToCart: (items: AddCartItemPayload[]) => void;
  removeFromCart: (items: CartItem[]) => void;
  baseCartAnalyticsArgs: any;
  isLoading: boolean;
}) => {
  const cartIsEmpty = !cart || cart.line_items.length === 0;

  if (cartIsEmpty) {
    return (
      <EmptyState
        test={vaginalTest}
        closeCart={closeCart}
        addToCart={addToCart}
        baseCartAnalyticsArgs={baseCartAnalyticsArgs}
      />
    );
  }
  return (
    <FullCartState
      cart={cart}
      test={vaginalTest}
      isLoading={isLoading}
      addToCart={addToCart}
      removeFromCart={removeFromCart}
      baseCartAnalyticsArgs={baseCartAnalyticsArgs}
    />
  );
};

const FullCartState = ({
  cart,
  test,
  isLoading,
  addToCart,
  removeFromCart,
  baseCartAnalyticsArgs,
}: {
  cart: Cart;
  test?: Test;
  isLoading: boolean;
  addToCart: (items: AddCartItemPayload[]) => void;
  removeFromCart: (items: CartItem[]) => void;
  baseCartAnalyticsArgs: any;
}) => {
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [
    showEligibleForMoreWarningBanner,
    setShowEligibleForMoreWarningBanner,
  ] = useState<boolean>(false);

  // Track which specific cart item is being removed
  const [removingItemId, setRemovingItemId] = useState<string | null>(null);
  const navigate = useNavigate();

  // Custom remove handler that manages individual loading states
  const handleRemoveFromCart = (items: CartItem[]) => {
    if (removingItemId !== null) {
      // Prevent multiple simultaneous removes
      return;
    }

    // For single item removal, use that item's ID. For multiple items (like bundles), use first item's ID
    const itemId = items.length > 0 ? items[0].id : null;
    if (itemId) {
      setRemovingItemId(itemId);
    }

    // Don't await - let usePanelCart handle its own async operations
    removeFromCart(items);

    // Reset individual loading state after a short delay
    setTimeout(() => {
      setRemovingItemId(null);
    }, 1000);
  };
  const {
    showCart,
    refetchCart,
    setShowCart,
    checkoutOnCart,
    getCartItemByEcommProductSku,
  } = usePanelCart();

  const { setCart } = useContext(CartContext);

  // Toggles in flight; separate from `submitting` so a toggle does not read as a checkout.
  // Disabling the controls meanwhile is also what keeps toggle responses in order.
  const [subscriptionPending, setSubscriptionPending] = useState(false);

  const persistSubscriptions = useCallback(
    async (items: CartItem[], isSubscription: boolean): Promise<boolean> => {
      setSubscriptionPending(true);
      setError("");
      try {
        const updatedCart = await cartService.setItemsSubscription(
          cart.id,
          items.map((item) => ({
            id: item.id,
            is_subscription: isSubscription,
          }))
        );
        if (!updatedCart)
          throw new Error("Subscription update returned no cart");
        setCart(updatedCart);
        return true;
      } catch (requestError: any) {
        // A 4xx carries the server's reason and will not succeed on retry.
        const serverMessage =
          typeof requestError?.response?.data === "string"
            ? requestError.response.data
            : undefined;
        setError(
          serverMessage ||
            "Failed to update subscription settings. Please try again."
        );
        orderMetrics.logCheckoutError(
          cart.id,
          "subscription_toggle",
          serverMessage || requestError?.message || "unknown",
          "cart"
        );
        return false;
      } finally {
        setSubscriptionPending(false);
      }
    },
    [cart.id, setCart]
  );
  const controlsDisabled = submitting || subscriptionPending;

  const CART_DISCLAIMER =
    "*Your specific prescriptions will be determined at the sole discretion of an Evvy-affiliated provider.";

  const carePageViewedKey = getCarePageVisitedLocalStorageKey(test?.hash || "");
  const hasVisitedCarePage = localStorage.getItem(carePageViewedKey)
    ? true
    : false;

  const bundleItems = cart.line_items.filter(
    (item) => item.item_type === "bundle-treatment"
  );

  const individualItems = cart.line_items.filter(
    (item) => item.item_type === "individual-treatment"
  );

  const productItemsInCart = cart.line_items.filter(
    (item) => item.item_type === "product"
  );

  // Separate male partner vouchers from other product items
  const malePartnerVouchers = productItemsInCart.filter(
    isMalePartnerTreatmentVoucher
  );
  const nonVoucherProductItems = productItemsInCart.filter(
    (item) => !isMalePartnerTreatmentVoucher(item)
  );

  // The server decides which product lines form a UTI test bundle and prices them;
  // the browser only maps its line ids back to items.
  const utiBundles = useMemo(
    () =>
      cart.uti_bundles.map((bundle) => ({
        bundle,
        items: cart.line_items.filter((item) =>
          bundle.cart_line_item_ids.includes(item.id)
        ),
      })),
    [cart.uti_bundles, cart.line_items]
  );

  const otherProductItems = useMemo(() => {
    const utiBundleItemIds = new Set(
      utiBundles.flatMap(({ items }) => items.map((item) => item.id))
    );
    return nonVoucherProductItems.filter(
      (item) => !utiBundleItemIds.has(item.id)
    );
  }, [nonVoucherProductItems, utiBundles]);

  const subtotalString = formatResolvedPriceString(cart.subtotal);

  // One predicate for the tri-state indicator and the set the toggle POSTs.
  const subscribableBundleItems = useMemo(
    () =>
      bundleItems.filter(
        (item) => item.treatment_product?.bundle_variant?.subscription_config
      ),
    [bundleItems]
  );
  const bundleContainsEligibleSubscriptionItems =
    subscribableBundleItems.length > 0;

  // Calculate subscription state for bundle items
  const bundleSubscriptionState = useMemo((): {
    state: "none" | "some" | "all";
    subscribedCount: number;
    totalSubscribableCount: number;
  } => {
    if (subscribableBundleItems.length === 0) {
      return { state: "none", subscribedCount: 0, totalSubscribableCount: 0 };
    }

    const subscribedCount = subscribableBundleItems.filter(
      (item) => item.selling_plan_id
    ).length;

    const totalSubscribableCount = subscribableBundleItems.length;

    if (subscribedCount === 0) {
      return { state: "none", subscribedCount, totalSubscribableCount };
    } else if (subscribedCount === totalSubscribableCount) {
      return { state: "all", subscribedCount, totalSubscribableCount };
    } else {
      return { state: "some", subscribedCount, totalSubscribableCount };
    }
  }, [subscribableBundleItems]);

  // The box is checked only when every item is subscribed, so a click on a partial set arrives checked=true.
  const handleBundleSubscriptionChange = async (checked: boolean) => {
    const persisted = await persistSubscriptions(
      subscribableBundleItems,
      checked
    );
    if (!persisted) return;

    // track analytics event
    if (checked) {
      trackCustomEvent(
        eventNames.SUBSCRIPTION_BUNDLE_SELECTED_ALL,
        baseCartAnalyticsArgs
      );
    } else {
      trackCustomEvent(
        eventNames.SUBSCRIPTION_BUNDLE_UNSELECTED_ALL,
        baseCartAnalyticsArgs
      );
    }
  };

  // The server picks the plan from the line's own variant; the client only says on or off.
  const handleItemSubscriptionChange = (item: CartItem, subscribe: boolean) =>
    persistSubscriptions([item], subscribe);

  useEffect(() => {
    // if user has not visited care page and is currently not on care page, show warning banner
    const currentPage = window.location.pathname;
    if (currentPage !== "/care/" && !hasVisitedCarePage) {
      setShowEligibleForMoreWarningBanner(true);
    }
  }, [hasVisitedCarePage]);

  // trigger analytics event on load. No refetch: a GET racing a toggle POST could undo it.
  useEffect(() => {
    if (showCart) {
      sendTreatmentCartViewedCart({
        upsellSkus: cart?.upsells?.products.map((product) => product.sku) || [],
        voucherInCart: malePartnerVouchers?.length > 0,
        isMember: !!cart?.upsells?.subscription_info?.next_charge_date,
        warningInCart: showEligibleForMoreWarningBanner,
        ...baseCartAnalyticsArgs,
      });
    } else {
      // on close cart, clear the error message
      setError("");
    }
  }, [showCart]);

  const createConsultAndGoToIntake = useCallback(
    async (
      pharmacyType: PharmacyType = "precision",
      purchaseType?: string,
      latestConsultNotReusable?: boolean
    ) => {
      if (!cart) {
        console.error("No cart found");
        setError("Error processing your request. Please try again.");
        return;
      }
      if (!pharmacyType) {
        console.error("No pharmacyType found");
        setError(
          "Please select a delivery method before completing your order."
        );
        return;
      }
      setSubmitting(true);
      setError("");

      const consultType =
        purchaseType === "ungated-rx" ? "ungated-rx" : "vaginitis";

      try {
        let consult = test?.latest_vaginitis_consult;
        if (!consult || latestConsultNotReusable) {
          const consultResponse = await careService.asyncCreateConsult({
            hash: test?.hash,
            purchase_type: purchaseType,
            type: consultType,
            pharmacy_type: pharmacyType,
          });
          consult = consultResponse.data;
        }

        if (!consult) {
          throw new Error("Consult not found");
        }

        if (consult.pharmacy_type !== pharmacyType) {
          await careService.updateConsultPharmacyType(
            consult.uid,
            pharmacyType
          );
        }

        // The ungated-rx intake has no intro page; a resumed consult that
        // already consented would otherwise strand the user on a blank route.
        navigate(
          getCareIntakeUrl(
            consult.type === "ungated-rx" ? "consent" : "intro",
            consult
          )
        );
      } catch (error) {
        console.error("Error creating consult", error);
        setError(
          `Error creating consult. Please try again or contact ${SUPPORT_EMAIL}`
        );
      } finally {
        setSubmitting(false);
      }
    },
    [cart, test, navigate]
  );

  const handleCheckout = useCallback(
    async (pharmacyType: PharmacyType = "precision") => {
      if (!cart) {
        console.error("No cart found");
        setError("Error processing your request. Please try again.");
        return;
      }

      setSubmitting(true);
      setError("");

      try {
        let consult = test?.latest_vaginitis_consult;
        const isConsultReusable = consult?.is_pre_submit;

        sendTreatmentCartClickedCheckout({
          warningInCart: showEligibleForMoreWarningBanner,
          purchaseType: cart.purchase_type,
          pharmacyType,
          ...baseCartAnalyticsArgs,
        });

        // DVY-4545: care program checkout started, with whether the retest add-on is in cart.
        if (bundleItems.length > 0) {
          const retestInCart = !!(
            getCartItemByEcommProductSku(VHT_TEST_SKU) ||
            getCartItemByEcommProductSku(EXPANDED_TEST_SKU)
          );
          sendCareProgramCheckoutStarted({
            testHash: test?.hash,
            checked: retestInCart,
          });
        }

        if (!cart.requires_consult || (consult && isConsultReusable)) {
          // if consult is reusable, then go to checkout directly
          try {
            const checkoutUrl = await checkoutOnCart(consult?.uid as string);
            redirectToCheckout(checkoutUrl);
          } catch (error) {
            console.error(error);
            setError(
              `Error checking out. Please try again or contact ${SUPPORT_EMAIL}`
            );
          }
        } else if (cart.requires_consult) {
          await createConsultAndGoToIntake(
            pharmacyType,
            cart.purchase_type,
            !isConsultReusable
          );
        }
        setSubmitting(false);
      } catch (error) {
        console.error("Error during checkout process", error);
        setError(
          `Error processing your request. Please try again or contact ${SUPPORT_EMAIL}`
        );
        setSubmitting(false);
      }
    },
    [
      cart,
      test,
      baseCartAnalyticsArgs,
      showEligibleForMoreWarningBanner,
      createConsultAndGoToIntake,
      checkoutOnCart,
      setError,
      setSubmitting,
      bundleItems,
      getCartItemByEcommProductSku,
    ]
  );

  return (
    <div>
      <div className="px-4 md:px-8 pb-8">
        {/* show male partner vouchers above bundle items */}
        {malePartnerVouchers.map((item) => (
          <div className={cn("mb-4")} key={`male-partner-voucher-${item.id}`}>
            <MalePartnerVoucherCartItem
              item={item}
              removeFromCart={handleRemoveFromCart}
              disabled={controlsDisabled}
              isRemoving={removingItemId === item.id}
              baseCartAnalyticsArgs={baseCartAnalyticsArgs}
            />
          </div>
        ))}
        {/* show bundle items if exist */}
        {bundleItems.length > 0 && (
          <BundleCartItem
            items={bundleItems}
            productItems={productItemsInCart}
            addToCart={addToCart}
            isLoading={isLoading}
            removeFromCart={handleRemoveFromCart}
            refetchCart={refetchCart}
            disabled={controlsDisabled}
            isRemoving={
              bundleItems.length > 0
                ? removingItemId === bundleItems[0].id
                : false
            }
            initializeExpanded={individualItems.length === 0}
            subscriptionInfo={cart?.upsells?.subscription_info}
            baseCartAnalyticsArgs={baseCartAnalyticsArgs}
            // Subscription props
            bundleContainsEligibleSubscriptionItems={
              bundleContainsEligibleSubscriptionItems
            }
            bundleSubscriptionState={bundleSubscriptionState}
            handleBundleSubscriptionChange={handleBundleSubscriptionChange}
            onItemSubscriptionChange={handleItemSubscriptionChange}
            treatmentBundle={cart.treatment_bundle}
          />
        )}
        {/* show UTI product bundles if exist */}
        {utiBundles.map(({ bundle, items }) => (
          <div
            className={cn("space-y-2 mt-2 mb-2")}
            key={`product-bundle-${bundle.bundle_group_id}`}
          >
            <ProductBundleCartItem
              bundle={bundle}
              items={items}
              removeFromCart={handleRemoveFromCart}
              isLoading={isLoading}
              disabled={controlsDisabled}
              isRemoving={items.some((item) => removingItemId === item.id)}
            />
          </div>
        ))}
        {/* show other product items if exist (excluding male partner vouchers and bundle items) */}
        {otherProductItems.map((item) => (
          <div className={cn("space-y-2 mt-2 mb-2")} key={`product-${item.id}`}>
            <CartLineItem
              item={item}
              removeFromCart={handleRemoveFromCart}
              isLoading={isLoading}
              disabled={controlsDisabled}
              isRemoving={removingItemId === item.id}
              subscriptionOption={item.selling_plan_id ?? undefined}
              onSubscriptionChange={(subscribe) =>
                handleItemSubscriptionChange(item, subscribe)
              }
              baseCartAnalyticsArgs={baseCartAnalyticsArgs}
            />
          </div>
        ))}
        {individualItems.length > 0 && (
          <div
            className={cn("flex flex-col gap-4", {
              "mt-4": bundleItems.length > 0,
            })}
          >
            {/* show individual items if exist */}
            {individualItems.map((item) => (
              <CartLineItem
                key={`alc-${item.id}`}
                item={item}
                removeFromCart={handleRemoveFromCart}
                isLoading={isLoading}
                disabled={controlsDisabled}
                isRemoving={removingItemId === item.id}
                subscriptionOption={item.selling_plan_id ?? undefined}
                onSubscriptionChange={(subscribe) =>
                  handleItemSubscriptionChange(item, subscribe)
                }
                baseCartAnalyticsArgs={baseCartAnalyticsArgs}
              />
            ))}
            {showEligibleForMoreWarningBanner && (
              <ErrorBanner parentAdditionalClassName="mt-6 md:mt-4">
                <span className="b2 medium">
                  You're eligible for{" "}
                  <Link
                    to="/care/"
                    onClick={() => {
                      sendTreatmentCartClickedViewMoreProducts({
                        warningInCart: showEligibleForMoreWarningBanner,
                        ...baseCartAnalyticsArgs,
                      });
                      // close cart
                      setShowCart(false);
                    }}
                    className="underline persistSize"
                  >
                    more treatments
                  </Link>
                  .
                </span>
              </ErrorBanner>
            )}
            {bundleItems.length === 0 && individualItems.length === 0 && (
              <CartLineItem
                freebieInfo={CONSULT_REVIEW_FREEBIE}
                removeFromCart={handleRemoveFromCart}
                isLoading={isLoading}
                disabled={controlsDisabled}
                isRemoving={false}
                baseCartAnalyticsArgs={baseCartAnalyticsArgs}
              />
            )}
          </div>
        )}
      </div>

      {/* checkout button pinned to bottom of parent container*/}
      <div className="sticky bottom-0 inset-x-0 px-4 md:px-8 py-5 border-t border-evvy-dark-beige bg-evvy-cream">
        {/* subtotal */}

        <div className="flex justify-between text-sm md:text-base medium mb-2.5">
          <div className="">Subtotal</div>
          {/* format number as price */}
          <div>{subtotalString}</div>
        </div>
        <div className="text-sm md:text-base mb-5">
          Shipping & taxes calculated at checkout
        </div>
        {error && (
          <ErrorBanner parentAdditionalClassName="mb-2">{error}</ErrorBanner>
        )}
        {/* checkout button */}
        <div className="my-4">
          <BlueRectangularButton
            text="complete order ->"
            fullWidth
            paddingXClass="px-5"
            paddingYClass="py-4"
            disabled={controlsDisabled}
            onClick={() => handleCheckout()}
          />
        </div>
        <div className="text-sm font-medium text-evvy-gray-500">
          {CART_DISCLAIMER}
        </div>
      </div>
    </div>
  );
};

const DottedSectionHeader = ({ title }: { title: string }) => {
  return (
    <div className="flex items-center">
      <div className="border-t border-dashed border-evvy-black/20 w-full" />
      <span className="b3 mb-0 pb-0 whitespace-nowrap mr-2 ml-2">{title}</span>
      <div className="border-t border-dashed border-evvy-black/20 w-full my-5" />
    </div>
  );
};

const DiscountedPrice = ({
  price,
  discountedPrice,
}: {
  price: number;
  discountedPrice: number;
}) => {
  return (
    <div className="flex justify-between my-auto">
      <div className="my-auto">
        {formatNumberToPriceString(discountedPrice)}
      </div>
      <div className="text-evvy-black/50 ml-1 my-auto">
        <EvvyMarkdown>{`~~${formatNumberToPriceString(price)}~~`}</EvvyMarkdown>
      </div>
    </div>
  );
};

const MembersRetest = ({
  subscriptionInfo,
  refetchCart,
  baseCartAnalyticsArgs,
}: {
  subscriptionInfo: SubscriptionInfo;
  refetchCart: () => void;
  baseCartAnalyticsArgs: any;
}) => {
  const [, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const nextShipmentDate = subscriptionInfo.next_charge_date;
  const showMoveSubscriptionCta = subscriptionInfo?.show_move_subscription_cta;

  return (
    <div className="pb-0">
      <div
        className={classNames("flex justify-between items-center", {
          "bg-evvy-silverfish/[0.33] pt-5 px-5 pb-0 rounded-lg":
            showMoveSubscriptionCta,
        })}
      >
        <div className="flex space-x-4 justify-between">
          <div
            className={classNames("bg-evvy-dark-cream rounded-full flex", {
              "h-16 w-20 sm:h-[80px] sm:w-[100px]": showMoveSubscriptionCta,
              "h-20 w-20 sm:h-[60px] sm:w-[75px]": !showMoveSubscriptionCta,
            })}
          >
            <img
              className="w-full object-cover rounded-lg"
              src={TestKitImg}
              alt="care packaging"
            />
          </div>
          <div
            className={classNames("w-full", {
              "flex items-center": !showMoveSubscriptionCta,
            })}
          >
            <div>
              <div className="medium my-auto pr-3 mb-2">
                Vaginal Health Retest
              </div>
              {showMoveSubscriptionCta && (
                <p className="b3 text-evvy-black/60">
                  We recommend retesting after your Complete Treatment Program.
                  Make sure you have your next test on hand.
                </p>
              )}
            </div>
            {nextShipmentDate && showMoveSubscriptionCta && (
              <div className="mb-4">
                <span className="b3 semibold">MY NEXT SHIP DATE</span>
                <span className="b3 ml-2">
                  {formatDate(strDateToTimestamp(nextShipmentDate))}
                </span>
              </div>
            )}
            {showMoveSubscriptionCta && (
              <button
                className={cn(
                  "p-2.5 w-full flex items-center border rounded bg-white duration-100 border-evvy-black hover:bg-evvy-blue"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsLoading(true);
                  sendClickedTestUpsellCTA({
                    sku: subscriptionInfo.sku,
                    isMember: !!subscriptionInfo.next_charge_date,
                    ...baseCartAnalyticsArgs,
                  });
                  subscriptionService.triggerNextCharge(
                    () => {
                      refetchCart();
                      setIsLoading(false);
                    },
                    (error) => {
                      setError(error);
                    }
                  );
                }}
              >
                <div className="flex w-full justify-between items-center">
                  {isLoading ? (
                    <LoadingSpinner />
                  ) : (
                    <div className="t4 w-full">Move up my test</div>
                  )}
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const BundleUpsellCard = ({
  upsellProducts,
  addToCart,
  removeFromCart,
  getCartItemByEcommProductSku,
}: {
  upsellProducts?: EcommProduct[];
  addToCart: (items: AddCartItemPayload[]) => void;
  removeFromCart?: (items: CartItem[]) => void;
  getCartItemByEcommProductSku?: (sku: string) => CartItem | undefined;
}) => {
  return (
    <div>
      <DottedSectionHeader title="Limited time offer" />
      <div
        className={classNames(
          "flex justify-between items-center bg-evvy-silverfish/[0.33] p-3 rounded-lg"
        )}
      >
        {upsellProducts?.map((product) => {
          return (
            <div key={product.id}>
              {product.discounted_price && (
                <div className="text-right -mt-[18px]">
                  <span className="uppercase text-center bg-dv-pond text-white rounded-full font-semibold text-[12px] px-2.5 py-0.5 ml-4">
                    {formatNumberToPriceString(
                      Number(product.price) - (product.discounted_price || 0)
                    )}{" "}
                    OFF
                  </span>
                </div>
              )}
              <div className="flex space-x-4 pt-3">
                <div className="bg-evvy-dark-cream rounded-full h-[80px] w-[110px] flex">
                  <img
                    className="w-full object-cover rounded-lg"
                    src={TestKitImg}
                    alt={product.name}
                  />
                </div>
                <div className="w-full">
                  <div>
                    <div className="medium my-auto pr-3 mb-2">
                      {product.name}
                    </div>
                    <p className="b3 text-evvy-black/60">
                      Track your treatment's success. Add a retest to your
                      program now and lock in your exclusive discount.
                    </p>
                  </div>
                  {(() => {
                    const cartItem = getCartItemByEcommProductSku?.(
                      product.sku
                    );
                    const inCart = !!cartItem;
                    return (
                      <button
                        className={cn(
                          "p-2.5 w-full flex items-center border rounded bg-white duration-100",
                          {
                            "border-evvy-dark-beige": inCart,
                            "border-black hover:bg-evvy-blue": !inCart,
                          }
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (inCart && removeFromCart && cartItem) {
                            removeFromCart([cartItem]);
                          } else {
                            addToCart([
                              {
                                item_type: "product",
                                quantity: 1,
                                product_id: product.id,
                                ecomm_product: product,
                              },
                            ]);
                          }
                        }}
                      >
                        <div className="flex w-full justify-between items-center">
                          <div className="t4">{inCart ? "Remove" : "Add"}</div>
                          {product.discounted_price && (
                            <DiscountedPrice
                              price={Number(product.price)}
                              discountedPrice={product.discounted_price}
                            />
                          )}
                        </div>
                      </button>
                    );
                  })()}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CheckmarkIcon = () => (
  <svg
    className="w-4 h-4 p-0.5 text-white"
    viewBox="0 0 12 9"
    fill="currentColor"
  >
    <path d="M4.5 6.293L1.707 3.5a1 1 0 00-1.414 1.414L4.5 9.121l7.207-7.207a1 1 0 00-1.414-1.414L4.5 6.293z" />
  </svg>
);

const BundleCartItem = ({
  items,
  addToCart,
  isLoading,
  refetchCart,
  removeFromCart,
  disabled,
  isRemoving = false,
  initializeExpanded,
  subscriptionInfo,
  baseCartAnalyticsArgs,
  bundleContainsEligibleSubscriptionItems,
  bundleSubscriptionState,
  handleBundleSubscriptionChange,
  onItemSubscriptionChange,
  treatmentBundle,
}: BundleCartItemProps) => {
  const [expanded, setExpanded] = useState<boolean>(initializeExpanded);
  const { cart, showCart, getCartItemByEcommProductSku } = usePanelCart();
  const upsellProducts = cart?.upsells?.products;
  const showMoveSubscriptionCta = subscriptionInfo?.next_charge_date;

  // Wrapper for removeFromCart to match the expected signature
  const handleRemoveFromCart = (items: CartItem[]) => {
    removeFromCart(items);
  };

  useEffect(() => {
    if (showCart) {
      setExpanded(initializeExpanded);
    } else {
      const timer = setTimeout(() => {
        setExpanded(initializeExpanded);
      }, 300); // delay 300ms until cart is closed to reset state

      return () => clearTimeout(timer);
    }
  }, [initializeExpanded, showCart]);

  return (
    <div className="rounded-xl bg-white p-4">
      <div className="flex justify-between items-start">
        <div className="flex gap-2">
          <div className="bg-evvy-dark-beige rounded h-20 w-[120px] overflow-hidden flex">
            <img
              className="w-full object-contain px-1.5 pt-2 pb-1.5"
              src={CarePackagingImg}
              alt="care packaging"
            />
          </div>
          <div className="flex flex-col gap-1 justify-center">
            <div className="font-medium text-evvy-black text-lg">
              Complete Treatment Program
            </div>
            {/* Null here means every bundle line failed to resolve, not "no bundle". */}
            {treatmentBundle ? (
              <div className="flex gap-1 items-start">
                <div className="font-semibold text-evvy-black text-sm">
                  {formatResolvedPriceString(treatmentBundle.subtotal)}
                </div>
                {Number(treatmentBundle.full_price) >
                  Number(treatmentBundle.subtotal) && (
                  <div className="text-evvy-gray-500 text-sm line-through">
                    {formatResolvedPriceString(treatmentBundle.full_price)}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-evvy-gray-500 text-sm">
                Pricing unavailable
              </div>
            )}
          </div>
        </div>
        {/* remove button */}
        <button
          onClick={() => removeFromCart(items)}
          disabled={disabled || isRemoving}
          className="p-1"
        >
          {isRemoving ? <LoadingSpinner /> : <TrashIcon className="w-5 h-5" />}
        </button>
      </div>

      {/* Add subscription checkbox directly below title */}
      {bundleContainsEligibleSubscriptionItems &&
        handleBundleSubscriptionChange &&
        bundleSubscriptionState && (
          <div className="flex gap-2 items-center pt-4">
            <input
              id="bundle-subscription-checkbox"
              type="checkbox"
              checked={bundleSubscriptionState.state === "all"}
              onChange={(e) => handleBundleSubscriptionChange(e.target.checked)}
              className="sr-only"
              disabled={disabled}
            />
            <label
              htmlFor="bundle-subscription-checkbox"
              className="flex gap-2 items-center cursor-pointer flex-1"
            >
              <div
                className={cn(
                  "w-4 h-4 rounded-sm flex items-center justify-center cursor-pointer",
                  {
                    "bg-evvy-pine border-evvy-pine":
                      bundleSubscriptionState.state === "all",
                    "bg-evvy-pine/50 border-evvy-pine/50":
                      bundleSubscriptionState.state === "some",
                    "bg-white border-gray-300 border":
                      bundleSubscriptionState.state === "none",
                  }
                )}
              >
                {bundleSubscriptionState.state === "all" && <CheckmarkIcon />}
                {bundleSubscriptionState.state === "some" && (
                  <svg
                    className="w-3 h-3 text-white"
                    viewBox="0 0 12 2"
                    fill="currentColor"
                  >
                    <rect x="2" y="0" width="8" height="2" rx="1" />
                  </svg>
                )}
              </div>
              <div
                className={cn(
                  "flex gap-1 items-center",
                  bundleSubscriptionState.state !== "none"
                    ? "text-evvy-black"
                    : "text-evvy-gray-500"
                )}
              >
                <span className="text-md font-medium leading-6">
                  Subscribe & save on eligible products
                </span>
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <SyncIcon className="w-3.5 h-3.5" />
                </div>
              </div>
            </label>
          </div>
        )}

      {/* show expanded items if expanded */}
      {expanded && (
        <>
          <div className="h-px bg-evvy-gray-100 my-4" />
          <div className="flex flex-col gap-4 pb-2">
            <button
              className="flex gap-2 items-center cursor-pointer linkedText"
              onClick={() => setExpanded(!expanded)}
            >
              <span className="text-sm italic text-evvy-gray-500">
                Hide Products
              </span>
              <div className="w-3 h-3 flex items-center justify-center transform scale-y-[-1]">
                <svg
                  className="w-3 h-3 text-evvy-gray-500"
                  viewBox="0 0 12 7"
                  fill="currentColor"
                >
                  <path
                    d="M1 1l5 5 5-5"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                  />
                </svg>
              </div>
            </button>
            <div className="space-y-4">
              {items.map((item) => (
                <IndividualBundleItem
                  key={item.id}
                  treatment={item.treatment_product}
                  item={item}
                  subscriptionOption={item.selling_plan_id ?? undefined}
                  onSubscriptionChange={onItemSubscriptionChange}
                  disabled={disabled}
                  baseCartAnalyticsArgs={baseCartAnalyticsArgs}
                />
              ))}
            </div>
          </div>
          <div className="border-t border-dashed border-evvy-gray-100 my-4" />
          {/* Freebie items */}
          <div className="flex flex-col gap-2 pb-2">
            <div className="text-sm italic text-evvy-gray-500">
              Included in Your Treatment Program
            </div>
            <div className="space-y-0">
              {TRANSPARENT_BUNDLE_FREEBIES.map((item, index) => (
                <div key={index} className="flex items-center gap-1">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <CheckMarkIcon className="text-evvy-pine" />
                  </div>
                  <div className="text-sm font-medium text-evvy-pine leading-6">
                    {item.mobileText}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {!expanded && (
        <>
          <div className="border-t border-dashed border-evvy-gray-100 my-4" />
          <button
            className="flex gap-2 items-center cursor-pointer linkedText"
            onClick={() => setExpanded(!expanded)}
          >
            <span className="text-sm italic text-evvy-gray-500">
              Show All Products
            </span>
            <div className="w-3 h-3 flex items-center justify-center">
              <svg
                className="w-3 h-3 text-evvy-gray-500"
                viewBox="0 0 12 7"
                fill="currentColor"
              >
                <path
                  d="M1 1l5 5 5-5"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
              </svg>
            </div>
          </button>
        </>
      )}

      <div className="mt-4">
        {showMoveSubscriptionCta && (
          <MembersRetest
            subscriptionInfo={subscriptionInfo}
            refetchCart={refetchCart}
            baseCartAnalyticsArgs={baseCartAnalyticsArgs}
          />
        )}
        {upsellProducts && upsellProducts?.length > 0 && (
          <BundleUpsellCard
            upsellProducts={upsellProducts}
            addToCart={addToCart}
            removeFromCart={handleRemoveFromCart}
            getCartItemByEcommProductSku={getCartItemByEcommProductSku}
          />
        )}
      </div>
    </div>
  );
};

const CartLineItem = ({
  item,
  freebieInfo = CONSULT_REVIEW_FREEBIE,
  isLoading = false,
  removeFromCart,
  disabled,
  isRemoving = false,
  subscriptionOption,
  onSubscriptionChange,
  baseCartAnalyticsArgs,
}: {
  item?: CartItem;
  freebieInfo?: CartFreebieItem;
  isLoading?: boolean;
  removeFromCart: (items: CartItem[]) => void;
  disabled?: boolean;
  isRemoving?: boolean;
  subscriptionOption?: string;
  onSubscriptionChange?: (subscribe: boolean) => Promise<boolean>;
  baseCartAnalyticsArgs: any;
}) => {
  const product = item?.ecomm_product;
  const treatment = item?.treatment_product;

  // Use backend-provided images first, then fall back to frontend form factor mapping
  // For UTI test products, use the product card image
  const image = treatment
    ? treatment.a_la_care_image_url ||
      (treatment.form_factor &&
        FORM_FACTOR_TO_IMAGE_V2[treatment.form_factor]?.image?.individual)
    : product
      ? product.sku === UTI_TEST_SKU
        ? UtiTestImg
        : product.image_url || TestKitImg
      : freebieInfo.imgSrc;

  const altText = treatment
    ? treatment.form_factor
    : product
      ? product.name
      : freebieInfo.altText;

  // Get subscription config - check both treatment and product subscription configs
  const subscriptionConfig =
    treatment?.ecomm_product?.subscription_config ||
    product?.subscription_config;
  const isSubscriptionEligible = !!subscriptionConfig;
  const isSubscribed = !!subscriptionOption;

  // Get subscription options from config
  const subscriptionPlans = subscriptionConfig?.selling_plans || {};
  const defaultPlanId = subscriptionConfig?.default_plan_id;
  const discountPercentage = defaultPlanId
    ? subscriptionPlans[defaultPlanId]?.discount_percentage
    : 0;

  return (
    <div className="rounded-xl bg-white p-3">
      <div className="flex justify-between items-start">
        <div className="flex gap-3">
          <div className="bg-evvy-dark-beige rounded h-[66px] w-[100px] sm:h-[80px] sm:w-[120px] overflow-hidden relative">
            <img
              className="w-full object-cover"
              loading="lazy"
              src={image}
              alt={altText}
            />
            <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-gradient-to-b from-transparent to-white pointer-events-none" />
          </div>
          <div className="flex flex-col gap-1">
            <div className="font-medium text-evvy-black text-lg leading-6">
              {treatment
                ? treatment.preview_title
                : product
                  ? product.name
                  : freebieInfo.mobileText}
            </div>
            <div className="flex gap-1 items-center text-evvy-gray-500 text-sm">
              {treatment ? treatment.quantity_text : ""}
            </div>
            <div className="flex gap-1 items-start">
              {treatment ? (
                <>
                  {(() => {
                    if (item?.unavailable) {
                      return (
                        <div className="text-evvy-gray-500 text-sm leading-6">
                          Unavailable
                        </div>
                      );
                    }
                    const basePrice = Number(item.price);
                    const effectivePrice = Number(item.effective_price);
                    return (
                      <>
                        <div className="font-semibold text-evvy-black text-sm leading-6">
                          {formatResolvedPriceString(effectivePrice)}
                        </div>
                        {effectivePrice < basePrice && (
                          <div className="text-evvy-gray-500 text-sm leading-6 line-through">
                            {formatResolvedPriceString(basePrice)}
                          </div>
                        )}
                        {treatment && isSubscribed && (
                          <div className="text-evvy-pine text-sm leading-6 font-semibold">
                            {discountPercentage}% Off
                          </div>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : product ? (
                (() => {
                  if (item?.unavailable) {
                    return (
                      <div className="text-evvy-gray-500 text-sm">
                        Unavailable
                      </div>
                    );
                  }
                  const effectivePrice = Number(item.effective_price);
                  // Catalog price on purpose: for a UTI-bundle half item.price is already the
                  // bundle-discounted base, so it would hide the discount.
                  const fullPrice = Number(product.price);
                  return (
                    <>
                      <div className="font-semibold text-evvy-black text-sm">
                        {formatResolvedPriceString(effectivePrice)}
                      </div>
                      {effectivePrice < fullPrice && (
                        <div className="text-evvy-gray-500 text-sm line-through">
                          {formatResolvedPriceString(fullPrice)}
                        </div>
                      )}
                    </>
                  );
                })()
              ) : (
                <>
                  Free{" "}
                  <span className="text-evvy-gray text-sm line-through">
                    {freebieInfo.price}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
        {/* remove button */}
        {item && (
          <button
            onClick={() => removeFromCart([item])}
            disabled={disabled || isRemoving}
            className="p-1"
          >
            {isRemoving ? (
              <LoadingSpinner />
            ) : (
              <TrashIcon className="w-5 h-5" />
            )}
          </button>
        )}
      </div>

      {/* Subscription checkbox */}
      {isSubscriptionEligible && item && onSubscriptionChange && (
        <div className="flex gap-2 items-center pt-4">
          <input
            id={`subscription-checkbox-${item.id}`}
            type="checkbox"
            checked={isSubscribed}
            onChange={async (e) => {
              const subscribe = e.target.checked;
              // Only count a toggle the server accepted.
              if (!(await onSubscriptionChange(subscribe))) return;
              trackCustomEvent(
                subscribe
                  ? eventNames.SUBSCRIPTION_ITEM_SELECTED
                  : eventNames.SUBSCRIPTION_ITEM_UNSELECTED,
                baseCartAnalyticsArgs
              );
            }}
            className="sr-only"
            disabled={disabled}
          />
          {treatment && defaultPlanId && subscriptionPlans?.[defaultPlanId] && (
            <label
              htmlFor={`subscription-checkbox-${item.id}`}
              className="flex gap-2 items-center cursor-pointer flex-1"
            >
              <div
                className={`w-4 h-4 rounded-sm border flex items-center justify-center ${
                  isSubscribed
                    ? "bg-evvy-pine border-evvy-pine"
                    : "bg-white border-gray-300"
                }`}
              >
                {isSubscribed && <CheckmarkIcon />}
              </div>
              <div
                className={cn(
                  "flex gap-1 items-center",
                  isSubscribed ? "text-evvy-black" : "text-evvy-gray-500"
                )}
              >
                <span className="text-md font-medium leading-6">
                  {`Subscribe & save — ships every ${subscriptionPlans[defaultPlanId].cadence}`}
                </span>
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <SyncIcon className="w-3.5 h-3.5" />
                </div>
              </div>
            </label>
          )}
        </div>
      )}
    </div>
  );
};

// The server groups and prices UTI test bundles (cart.uti_bundles); this renders one.
const ProductBundleCartItem = ({
  bundle,
  items,
  removeFromCart,
  isLoading,
  disabled,
  isRemoving = false,
}: {
  bundle: UtiBundle;
  items: CartItem[];
  removeFromCart: (items: CartItem[]) => void;
  isLoading?: boolean;
  disabled?: boolean;
  isRemoving?: boolean;
}) => {
  const effectivePrice = Number(bundle.subtotal);
  const basePrice = Number(bundle.full_price);
  const bundleName = "Evvy Vaginal Microbiome with UTI+ Test";

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="rounded-xl bg-white p-3">
      <div className="flex justify-between items-start">
        <div className="flex gap-3">
          {/* Image */}
          <div className="bg-evvy-dark-beige rounded h-[66px] w-[100px] sm:h-[80px] sm:w-[120px] overflow-hidden relative">
            <img
              className="w-full object-cover"
              loading="lazy"
              src={VhtUtiBundleImg}
              alt={bundleName}
            />
            <div className="absolute bottom-0 left-0 right-0 h-[30%] bg-gradient-to-b from-transparent to-white pointer-events-none" />
          </div>
          {/* Content */}
          <div className="flex flex-col gap-1">
            <div className="font-medium text-evvy-black text-lg leading-6">
              {bundleName}
            </div>
            <div className="flex gap-1 items-start">
              <div className="font-semibold text-evvy-black text-sm leading-6">
                {formatResolvedPriceString(effectivePrice)}
              </div>
              {effectivePrice < basePrice && (
                <div className="text-evvy-gray-500 text-sm leading-6 line-through">
                  {formatResolvedPriceString(basePrice)}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Remove button */}
        <button
          onClick={() => removeFromCart(items)}
          disabled={disabled || isRemoving}
          className="p-1"
        >
          {isRemoving ? <LoadingSpinner /> : <TrashIcon className="w-5 h-5" />}
        </button>
      </div>
    </div>
  );
};

const IndividualBundleItem = ({
  treatment,
  freebieInfo = TRANSPARENT_BUNDLE_FREEBIES[0],
  item,
  subscriptionOption,
  onSubscriptionChange,
  disabled,
  baseCartAnalyticsArgs,
}: {
  treatment?: Prescription;
  freebieInfo?: CartFreebieItem;
  item?: CartItem;
  subscriptionOption?: string;
  onSubscriptionChange?: (
    item: CartItem,
    subscribe: boolean
  ) => Promise<boolean>;
  disabled?: boolean;
  baseCartAnalyticsArgs: any;
}) => {
  const image = treatment
    ? treatment.treatment_plan_image_url ||
      (treatment.form_factor &&
        FORM_FACTOR_TO_IMAGE_V2[treatment.form_factor]?.image?.individual)
    : freebieInfo.imgSrc;

  const altText = treatment ? treatment.form_factor : freebieInfo.altText;

  // Get subscription config
  const subscriptionConfig = treatment?.bundle_variant?.subscription_config;
  const isSubscriptionEligible = !!subscriptionConfig;
  const isSubscribed = !!subscriptionOption;

  // Get subscription options from config
  const subscriptionPlans = subscriptionConfig?.selling_plans || {};
  const defaultPlanId = subscriptionConfig?.default_plan_id;
  const discountPercentage = defaultPlanId
    ? subscriptionPlans[defaultPlanId]?.discount_percentage
    : 0;

  return (
    <div className="flex gap-3">
      <div className="bg-evvy-dark-beige rounded-md h-[72px] w-[72px] overflow-hidden flex">
        <img
          className="w-full object-contain"
          loading="lazy"
          src={image}
          alt={altText}
        />
      </div>
      <div className="flex flex-col gap-1 flex-grow">
        <div className="font-medium text-evvy-black text-base leading-6">
          {treatment ? treatment.preview_title : freebieInfo.mobileText}
        </div>
        <div className="flex gap-1">
          {item?.unavailable ? (
            <div className="text-evvy-gray-500 text-sm leading-6">
              Unavailable
            </div>
          ) : (
            <>
              <div className="font-medium text-evvy-black text-sm leading-6">
                {treatment
                  ? formatResolvedPriceString(item?.effective_price ?? null)
                  : "Free"}
              </div>
              <div className="text-evvy-gray-500 text-sm leading-6 line-through">
                {treatment
                  ? formatNumberToPriceString(
                      Number(treatment?.ecomm_product.price)
                    )
                  : freebieInfo.price}
              </div>
              {treatment && (
                <div className="text-evvy-pine text-sm leading-6 font-semibold">
                  {isSubscribed ? discountPercentage + 10 : 10}% Off
                </div>
              )}
            </>
          )}
        </div>

        {/* Subscription checkbox */}
        {isSubscriptionEligible &&
          item &&
          onSubscriptionChange &&
          defaultPlanId && (
            <div className="flex gap-2 items-center pt-1">
              <input
                id={`individual-subscription-checkbox-${item?.id}`}
                type="checkbox"
                checked={isSubscribed}
                onChange={async (e) => {
                  const subscribe = e.target.checked;
                  // Only count a toggle the server accepted.
                  if (!(await onSubscriptionChange(item, subscribe))) return;
                  trackCustomEvent(
                    subscribe
                      ? eventNames.SUBSCRIPTION_ITEM_SELECTED
                      : eventNames.SUBSCRIPTION_ITEM_UNSELECTED,
                    baseCartAnalyticsArgs
                  );
                }}
                className="sr-only"
                disabled={disabled}
              />
              <label
                htmlFor={`individual-subscription-checkbox-${item?.id}`}
                className="flex gap-2 items-center cursor-pointer flex-1"
              >
                <div
                  className={`w-4 h-4 rounded-sm border flex items-center justify-center ${
                    isSubscribed
                      ? "bg-evvy-pine border-evvy-pine"
                      : "bg-white border-gray-300"
                  }`}
                >
                  {isSubscribed && <CheckmarkIcon />}
                </div>
                <div
                  className={cn(
                    "flex gap-1 items-center",
                    isSubscribed ? "text-evvy-black" : "text-evvy-gray-500"
                  )}
                >
                  <span className="text-sm">
                    {`Ships every ${subscriptionPlans[defaultPlanId].cadence}`}
                  </span>
                  <div className="w-3.5 h-3.5 flex items-center justify-center">
                    <SyncIcon className="w-3.5 h-3.5" />
                  </div>
                </div>
              </label>
            </div>
          )}
      </div>
    </div>
  );
};

const MalePartnerVoucherCartItem = ({
  item,
  removeFromCart,
  disabled,
  isRemoving = false,
  baseCartAnalyticsArgs,
}: {
  item: CartItem;
  removeFromCart: (items: CartItem[]) => void;
  disabled?: boolean;
  isRemoving?: boolean;
  baseCartAnalyticsArgs: any;
}) => {
  const product = item.ecomm_product;

  return (
    <div className="rounded-xl bg-white p-4">
      <div className="flex justify-between items-start">
        <div className="flex gap-3">
          <div className="bg-evvy-dark-beige rounded-md h-[66px] w-[99px] overflow-hidden flex">
            {product.image_url ? (
              <img
                className="w-full object-contain"
                loading="lazy"
                src={product.image_url}
                alt={product.name}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-evvy-blue to-evvy-dark-blue">
                <div className="text-white text-center">
                  <div className="text-lg font-bold">🎁</div>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1 flex-grow">
            <div className="font-medium text-evvy-black text-base leading-6">
              {product.name || "Male Partner BV Treatment Voucher"}
            </div>
            <div className="flex gap-1">
              <div className="font-medium text-evvy-black text-sm leading-6">
                {item.unavailable
                  ? "Unavailable"
                  : formatResolvedPriceString(item.effective_price)}
              </div>
            </div>
          </div>
        </div>
        {/* remove button */}
        <button
          onClick={() => removeFromCart([item])}
          disabled={disabled || isRemoving}
          className="p-1"
        >
          {isRemoving ? <LoadingSpinner /> : <TrashIcon className="w-5 h-5" />}
        </button>
      </div>

      {/* Instructions section with dotted line */}
      <div className="border-t border-dashed border-evvy-gray-100 mt-4 pt-4">
        <div className="text-sm text-evvy-gray-500">
          We'll send you an email voucher for your male partner to redeem
          treatment. An Evvy clinician will determine which, if any, treatment
          is right for them. If for any reason this is not right for your
          partner, you'll be refunded.
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({
  test,
  closeCart,
  addToCart,
  baseCartAnalyticsArgs,
}: {
  test?: Test;
  closeCart: () => void;
  addToCart: (items: AddCartItemPayload[]) => void;
  baseCartAnalyticsArgs: any;
}) => {
  const navigate = useNavigate();
  const { showCart } = usePanelCart();
  const eligibleForProgramCare = test?.eligible_for_care;
  const closeCartAndGoToCare = () => {
    closeCart();
    // navigate to care
    navigate("/care/");
  };

  // trigger analytics event on load
  useEffect(() => {
    if (showCart) {
      sendTreatmentCartViewedCart(baseCartAnalyticsArgs);
    }
  }, [baseCartAnalyticsArgs, showCart]);

  return test && eligibleForProgramCare ? (
    <EmptyStateProgramCare
      test={test}
      closeCartAndGoToCare={closeCartAndGoToCare}
      addToCart={addToCart}
      baseCartAnalyticsArgs={baseCartAnalyticsArgs}
    />
  ) : (
    <EmptyStateALaCare
      closeCartAndGoToCare={closeCartAndGoToCare}
      baseCartAnalyticsArgs={baseCartAnalyticsArgs}
    />
  );
};

const EmptyStateALaCare = ({
  closeCartAndGoToCare,
  baseCartAnalyticsArgs,
}: {
  closeCartAndGoToCare: () => void;
  baseCartAnalyticsArgs: any;
}) => {
  return (
    <>
      <div className="space-y-6 md:space-y-8 pb-5 md:pb-7 px-4 md:px-7">
        <div className="b1 medium md:text-lg/[24px] text-center">
          Looking kind of empty in here.<br></br>We recommend:
        </div>
        <div className="rounded-2xl md:mx-3">
          <div className="h-52 bg-evvy-silverfish rounded-t-2xl">
            <div className="relative h-full">
              <img
                alt="Individual Rx Treatments"
                className="h-44 mx-auto object-fill cursor-pointer rounded-t-2xl"
                src={RainingPrescriptionsImg}
                onClick={closeCartAndGoToCare}
              />
              <div className="absolute right-0 left-0 top-20 bottom-0 bg-gradient-to-b from-transparent to-evvy-white pointer-events-none"></div>
            </div>
          </div>
          <div className="bg-evvy-white px-4 md:px-7 space-y-6 pb-4 md:pb-7 rounded-b-2xl">
            <div>
              <h4 className="md:text-2xl mb-2">Individual Rx Treatments</h4>
              <div className="text-evvy-black/50">
                Customized plan for your unique symptoms and needs
              </div>
            </div>
            <RectangularButton
              onClick={() => {
                // close panel
                sendTreatmentClickedEmptyStateCTA({
                  ctaText: "Browse all care",
                  ...baseCartAnalyticsArgs,
                });
                closeCartAndGoToCare();
              }}
              text={"Browse all care ->"}
              textColorClass="text-evvy-black"
              fullWidth
              bgColorClass="bg-transparent"
              borderColor="border-evvy-black"
            />
          </div>
        </div>
      </div>
    </>
  );
};

// Format all bundle treatments into AddCartItemPayload
export const getBundleTreatmentsFromPayload = (
  transparentCareTreatments?: TransparentCareRecommendations
) => {
  const { treat, rebuild, relief } =
    transparentCareTreatments?.transparent_bundle?.treatments || {};
  const allBundleTreatments = [
    ...(treat?.treatments || []),
    ...(rebuild?.treatments || []),
    ...(relief?.treatments || []),
  ];
  const itemsToAdd = allBundleTreatments.map((treatment) => {
    return {
      item_type: "bundle-treatment" as CART_ITEM_TYPE,
      quantity: 1,
      treatment_product_id: treatment.id,
      treatment_product: treatment,
    };
  });
  return itemsToAdd;
};

const EmptyStateProgramCare = ({
  test,
  closeCartAndGoToCare,
  addToCart,
  baseCartAnalyticsArgs,
}: {
  test: Test;
  closeCartAndGoToCare: () => void;
  addToCart: (items: AddCartItemPayload[]) => void;
  baseCartAnalyticsArgs: any;
}) => {
  const { transparentCareTreatments } = useTransparentCareTreatmentsContext();
  const treatmentsToAdd = getBundleTreatmentsFromPayload(
    transparentCareTreatments
  );

  const addTransparentBundleToCart = useCallback(() => {
    addToCart(treatmentsToAdd);
    sendTreatmentClickedEmptyStateCTA({
      ctaText: "Add to cart",
      ...baseCartAnalyticsArgs,
    });
  }, [treatmentsToAdd]);

  return (
    <>
      <div className="space-y-6 md:space-y-8 pb-5 md:pb-7 px-4 md:px-7">
        <div className="b1 medium md:text-lg/[24px] text-center">
          Looking kind of empty in here.<br></br>We recommend:
        </div>
        <div className="rounded-2xl md:mx-3">
          <div className="h-60 overflow-hidden bg-evvy-dark-cream rounded-t-2x pt-6">
            <img
              alt="The Complete Treatment Program"
              className="mx-auto object-contain h-44 cursor-pointer"
              src={CarePackagingImg}
              onClick={closeCartAndGoToCare}
            />
          </div>
          <div className="-mt-14 h-14 bg-gradient-to-b from-transparent to-evvy-white" />
          <div className="bg-evvy-white px-4 md:px-7 space-y-6 pb-4 md:pb-7 rounded-b-2xl">
            <div>
              <h4 className="md:text-2xl mb-2">
                The Complete Treatment Program
              </h4>
              <div>Customized plan for your unique symptoms and needs</div>
            </div>
            <BlueRectangularButton
              text=""
              fullWidth
              paddingXClass="px-5"
              paddingYClass="py-4"
              // TODO: analytics
              onClick={addTransparentBundleToCart}
            >
              <div className="flex justify-between">
                <div>Add to cart</div>
                <div>
                  {formatNumberToPriceString(
                    transparentCareTreatments?.transparent_bundle?.pricing
                      ?.bundled_price || 0
                  )}
                  <span className="ml-1 text-evvy-black/50">
                    <EvvyMarkdown>{`~~${formatNumberToPriceString(transparentCareTreatments?.transparent_bundle?.pricing?.full_price || 0)}~~`}</EvvyMarkdown>
                  </span>
                </div>
              </div>
            </BlueRectangularButton>
          </div>
        </div>
      </div>
      <div className="border-t border-evvy-dark-beige py-5">
        <RectangularButton
          onClick={() => {
            // trigger analytics
            sendTreatmentClickedEmptyStateCTA({
              ctaText: "Browse all care",
              ...baseCartAnalyticsArgs,
            });
            // close panel
            closeCartAndGoToCare();
          }}
          text={"Browse all care ->"}
          textColorClass="text-evvy-black"
          fullWidth
          bgColorClass="bg-transparent"
        />
      </div>
    </>
  );
};

export const PanelCartComponent = ({
  cart,
  showCart,
  setShowCart,
  vaginalTest,
  isLoading,
  addToCart,
  removeFromCart,
  baseCartAnalyticsArgs,
}: {
  cart?: Cart;
  showCart: boolean;
  setShowCart: (show: boolean) => void;
  vaginalTest?: Test;
  isLoading: boolean;
  addToCart: (items: AddCartItemPayload[]) => void;
  refetchCart?: () => void;
  removeFromCart: (items: CartItem[]) => void;
  baseCartAnalyticsArgs: any;
}) => {
  // The panel's own checkout button sits at the bottom of the screen, and the panel outlives
  // the pages that open it, so it reports its own occupancy rather than leaving it to the
  // program sticky CTA (which unmounts on navigation).
  useEffect(() => {
    setBottomCtaOccupied("cart-panel", showCart);
    return () => setBottomCtaOccupied("cart-panel", false);
  }, [showCart]);
  // Lets chat cards link to a page with a panel when this page has none.
  useEffect(registerCartPanel, []);

  return (
    <Panel handleClose={() => setShowCart(false)} isOpen={showCart}>
      <UserCart
        cart={cart}
        vaginalTest={vaginalTest}
        isLoading={isLoading}
        addToCart={addToCart}
        removeFromCart={removeFromCart}
        closeCart={() => setShowCart(false)}
        baseCartAnalyticsArgs={baseCartAnalyticsArgs}
      />
    </Panel>
  );
};
